/**
 * Device Management Service
 * 
 * Handles device identification, registration, and sync coordination
 * for cross-device chat synchronization.
 */

import { nanoid } from 'nanoid';

export interface DeviceInfo {
  deviceId: string;
  deviceType: 'web' | 'mobile' | 'tablet' | 'desktop';
  deviceName: string;
  userAgent: string;
  lastSeen: Date;
}

export interface SyncStatus {
  isOnline: boolean;
  lastSyncSequence: number;
  lastSyncTime: Date;
  pendingMessages: number;
}

class DeviceManager {
  private deviceId: string;
  private deviceInfo: DeviceInfo;
  private syncStatus: SyncStatus;

  constructor() {
    this.deviceId = this.getOrCreateDeviceId();
    this.deviceInfo = this.generateDeviceInfo();
    this.syncStatus = {
      isOnline: navigator.onLine,
      lastSyncSequence: 0,
      lastSyncTime: new Date(),
      pendingMessages: 0
    };

    this.setupEventListeners();
  }

  /**
   * Get or create a persistent device ID
   */
  private getOrCreateDeviceId(): string {
    const stored = localStorage.getItem('veritus-device-id');
    if (stored) {
      return stored;
    }

    const newId = nanoid();
    localStorage.setItem('veritus-device-id', newId);
    return newId;
  }

  /**
   * Generate device information based on browser/environment
   */
  private generateDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    
    // Detect device type
    let deviceType: DeviceInfo['deviceType'] = 'web';
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      if (/iPad/.test(userAgent)) {
        deviceType = 'tablet';
      } else {
        deviceType = 'mobile';
      }
    }

    // Generate device name
    let deviceName = 'Unknown Device';
    if (userAgent.includes('Chrome')) {
      deviceName = 'Chrome Browser';
    } else if (userAgent.includes('Firefox')) {
      deviceName = 'Firefox Browser';
    } else if (userAgent.includes('Safari')) {
      deviceName = 'Safari Browser';
    } else if (userAgent.includes('Edge')) {
      deviceName = 'Edge Browser';
    }

    // Add OS info
    if (userAgent.includes('Windows')) {
      deviceName += ' (Windows)';
    } else if (userAgent.includes('Mac')) {
      deviceName += ' (macOS)';
    } else if (userAgent.includes('Linux')) {
      deviceName += ' (Linux)';
    } else if (userAgent.includes('Android')) {
      deviceName += ' (Android)';
    } else if (userAgent.includes('iOS')) {
      deviceName += ' (iOS)';
    }

    return {
      deviceId: this.deviceId,
      deviceType,
      deviceName,
      userAgent,
      lastSeen: new Date()
    };
  }

  /**
   * Setup event listeners for online/offline status
   */
  private setupEventListeners(): void {
    window.addEventListener('online', () => {
      this.syncStatus.isOnline = true;
      this.notifyStatusChange();
    });

    window.addEventListener('offline', () => {
      this.syncStatus.isOnline = false;
      this.notifyStatusChange();
    });

    // Update last seen periodically
    setInterval(() => {
      this.deviceInfo.lastSeen = new Date();
    }, 30000); // Every 30 seconds
  }

  /**
   * Get current device information
   */
  getDeviceInfo(): DeviceInfo {
    return { ...this.deviceInfo };
  }

  /**
   * Get device ID
   */
  getDeviceId(): string {
    return this.deviceId;
  }

  /**
   * Get current sync status
   */
  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  /**
   * Update sync status
   */
  updateSyncStatus(updates: Partial<SyncStatus>): void {
    this.syncStatus = { ...this.syncStatus, ...updates };
    this.notifyStatusChange();
  }

  /**
   * Register device with backend
   */
  async registerDevice(apiRequest: Function): Promise<boolean> {
    try {
      const response = await apiRequest('POST', '/api/sync/device', {
        deviceId: this.deviceInfo.deviceId,
        deviceType: this.deviceInfo.deviceType,
        deviceName: this.deviceInfo.deviceName
      });

      if (response.success) {
        console.log('✅ Device registered successfully:', this.deviceInfo.deviceId);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Failed to register device:', error);
      return false;
    }
  }

  /**
   * Get headers for sync API requests
   */
  getSyncHeaders(): Record<string, string> {
    return {
      'X-Device-Id': this.deviceId,
      'X-Device-Type': this.deviceInfo.deviceType
    };
  }

  /**
   * Status change notification (for React hooks to subscribe)
   */
  private statusChangeListeners: Array<(status: SyncStatus) => void> = [];

  onStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.statusChangeListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.statusChangeListeners.indexOf(callback);
      if (index > -1) {
        this.statusChangeListeners.splice(index, 1);
      }
    };
  }

  private notifyStatusChange(): void {
    this.statusChangeListeners.forEach(callback => {
      callback(this.getSyncStatus());
    });
  }

  /**
   * Clear device data (for logout)
   */
  clearDeviceData(): void {
    localStorage.removeItem('veritus-device-id');
    localStorage.removeItem('veritus-offline-messages');
    localStorage.removeItem('veritus-last-sync-sequence');
  }

  /**
   * Store offline message
   */
  storeOfflineMessage(message: any): void {
    const stored = localStorage.getItem('veritus-offline-messages');
    const messages = stored ? JSON.parse(stored) : [];
    
    messages.push({
      ...message,
      clientMessageId: message.clientMessageId || nanoid(),
      timestamp: new Date().toISOString(),
      deviceId: this.deviceId
    });
    
    localStorage.setItem('veritus-offline-messages', JSON.stringify(messages));
    this.syncStatus.pendingMessages = messages.length;
    this.notifyStatusChange();
  }

  /**
   * Get offline messages
   */
  getOfflineMessages(): any[] {
    const stored = localStorage.getItem('veritus-offline-messages');
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Clear offline messages
   */
  clearOfflineMessages(): void {
    localStorage.removeItem('veritus-offline-messages');
    this.syncStatus.pendingMessages = 0;
    this.notifyStatusChange();
  }

  /**
   * Get last sync sequence
   */
  getLastSyncSequence(): number {
    const stored = localStorage.getItem('veritus-last-sync-sequence');
    return stored ? parseInt(stored, 10) : 0;
  }

  /**
   * Update last sync sequence
   */
  updateLastSyncSequence(sequence: number): void {
    localStorage.setItem('veritus-last-sync-sequence', sequence.toString());
    this.syncStatus.lastSyncSequence = sequence;
    this.syncStatus.lastSyncTime = new Date();
    this.notifyStatusChange();
  }
}

// Singleton instance
let deviceManagerInstance: DeviceManager | null = null;

export function getDeviceManager(): DeviceManager {
  if (!deviceManagerInstance) {
    deviceManagerInstance = new DeviceManager();
  }
  return deviceManagerInstance;
}

export { DeviceManager };